import * as Sentry from '@sentry/browser';
import { reactRouterV5Instrumentation } from '@sentry/react';
import { StripeError } from '@stripe/stripe-js';
import { History } from 'history';
import { appConfig } from '../../appConfig';

interface SentryOrderCustomerRequestContext {
  worker: string;
  orderId?: string;
  customerId?: string;
  currentCustomerId?: string;
  [key: string]: string | undefined;
}

export interface SentryOrderCustomerFetchContext extends SentryOrderCustomerRequestContext {}

export interface SentryOrderCustomerActionContext {
  worker: SentryOrderCustomerRequestContext['worker'];
  orderId?: SentryOrderCustomerRequestContext['orderId'];
  customerId?: SentryOrderCustomerRequestContext['customerId'];
  paymentType?: string;
  [key: string]: string | undefined;
}

export interface SentryStripeErrorContext extends SentryOrderCustomerActionContext {
  step: 'create_payment_method' | 'confirm_card_payment' | 'retrieve_payment_intent' | 'resume_authentication';
}

export function initialiseSentry(history: History) {
  if (!appConfig.sentry.enabled || !appConfig.sentry.dsn) {
    return;
  }

  Sentry.init({
    dsn: appConfig.sentry.dsn,
    environment: appConfig.sentry.environment,
    tracesSampleRate: appConfig.sentry.tracesSampleRate,
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: reactRouterV5Instrumentation(history),
      }),
    ],
    tracePropagationTargets: [appConfig.api.basePath],
    ignoreErrors: [
      // Suppress common benign browser extension noise
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],
  });
}

export function setSentryUser(user: { id: string; userTypeId?: string } | null) {
  if (!appConfig.sentry.enabled) {
    return;
  }
  if (user) {
    Sentry.setUser({ id: user.id, userTypeId: user.userTypeId });
  } else {
    Sentry.setUser(null);
  }
}

function isExpectedHttpError(error: unknown): boolean {
  // 401 means the session expired and the app handles logout itself; not worth reporting.
  const status = (error as any)?.response?.status;
  return status === 401;
}

function captureOrderCustomerRequestError(
  error: unknown,
  context: SentryOrderCustomerRequestContext,
  feature: string
) {
  if (!appConfig.sentry.enabled) {
    return;
  }

  if (isExpectedHttpError(error)) {
    addOrderCustomerRequestBreadcrumb('Unauthenticated request - session expired', context, feature);
    return;
  }

  Sentry.withScope((scope: Sentry.Scope) => {
    scope.setTag('feature', feature);
    scope.setTag('worker', context.worker);

    const extra: Record<string, string | undefined> = {
      orderId: context.orderId,
      customerId: context.customerId,
    };

    const { worker, orderId, customerId, ...rest } = context;
    Object.assign(extra, rest);

    if (extra.paymentType) {
      scope.setTag('paymentType', extra.paymentType);
    }

    scope.setExtras(extra);
    scope.setLevel('error');
    Sentry.captureException(error);
  });
}

function addOrderCustomerRequestBreadcrumb(
  message: string,
  context: Partial<SentryOrderCustomerRequestContext>,
  feature: string
) {
  if (!appConfig.sentry.enabled) {
    return;
  }
  Sentry.addBreadcrumb({
    category: feature,
    message,
    data: context,
    level: 'info',
  });
}

export function captureOrderCustomerFetchError(
  error: unknown,
  context: SentryOrderCustomerFetchContext
) {
  captureOrderCustomerRequestError(error, context, 'order-customer-fetch');
}

export function addOrderCustomerFetchBreadcrumb(
  message: string,
  context: Partial<SentryOrderCustomerFetchContext>
) {
  addOrderCustomerRequestBreadcrumb(message, context, 'order-customer-fetch');
}

export function captureOrderCustomerActionError(
  error: unknown,
  context: SentryOrderCustomerActionContext
) {
  captureOrderCustomerRequestError(error, context, 'order-customer-actions');
}

export function addOrderCustomerActionBreadcrumb(
  message: string,
  context: Partial<SentryOrderCustomerActionContext>
) {
  addOrderCustomerRequestBreadcrumb(message, context, 'order-customer-actions');
}

// Stripe reports the tenant's own outcome (a decline, an invalid card field) with these types. A failed or
// abandoned 3DS challenge comes back as an invalid_request_error instead, so it is recognised by its code.
// The tenant sees all of these on screen and they are not failures of ours, so they only leave a breadcrumb.
// Every other error (API, connection, other invalid requests) means the payment flow itself is broken.
const TENANT_OUTCOME_ERROR_TYPES: StripeError['type'][] = ['card_error', 'validation_error'];
const TENANT_OUTCOME_ERROR_CODES = ['payment_intent_authentication_failure'];

function isTenantOutcome(error: StripeError): boolean {
  return TENANT_OUTCOME_ERROR_TYPES.includes(error.type) || TENANT_OUTCOME_ERROR_CODES.includes(error.code ?? '');
}

export function captureStripeError(error: StripeError, context: SentryStripeErrorContext) {
  if (!appConfig.sentry.enabled) {
    return;
  }

  const title = `Stripe ${error.type} during ${context.step}`;
  if (isTenantOutcome(error)) {
    addOrderCustomerActionBreadcrumb(`${title}: ${error.code || error.message}`, context);
    return;
  }

  Sentry.withScope((scope: Sentry.Scope) => {
    scope.setTag('feature', 'order-customer-actions');
    scope.setTag('worker', context.worker);
    scope.setTag('stripeErrorType', error.type);
    if (error.code) {
      scope.setTag('stripeErrorCode', error.code);
    }
    scope.setExtras({
      ...context,
      paymentIntentId: error.payment_intent?.id,
      intentStatus: error.payment_intent?.status,
    });
    scope.setLevel('error');
    // Stripe's messages can carry intent ids, so grouping by message would split one failure into many issues.
    scope.setFingerprint(['stripe', context.step, error.type, error.code || 'no_code']);
    Sentry.captureMessage(`${title}: ${error.message}`);
  });
}
